import type { ComponentPropsWithoutRef, Ref } from 'react'
import { forwardRef } from 'react'

import { cn } from '@adverity/ds-tokens/cn'
import { type VariantProps, cva } from '@adverity/ds-tokens/cva'
import { Slot } from '@radix-ui/react-slot'

const headingVariants = cva('font-regular', {
    variants: {
        level: {
            1: 'text-heading-1',
            2: 'text-heading-2',
            3: 'text-heading-3',
        },
    },
    defaultVariants: {
        level: 1,
    },
})

type HeadingVariants = VariantProps<typeof headingVariants>

type HeadingLevel = NonNullable<HeadingVariants['level']>

type HeadingProps = ComponentPropsWithoutRef<'h1'> & {
    asChild?: boolean
    level?: HeadingLevel
}

const levelToTag = {
    1: 'h1',
    2: 'h2',
    3: 'h3',
} as const

const Heading = forwardRef<HTMLElement, HeadingProps>(({ asChild, className, level = 1, ...props }, ref) => {
    const Component = asChild === true ? Slot : levelToTag[level]

    return (
        <Component
            ref={ref as Ref<HTMLHeadingElement>}
            className={cn(headingVariants({ level }), className)}
            {...props}
        />
    )
})

Heading.displayName = 'Heading'

export { Heading, headingVariants }
export type { HeadingProps, HeadingVariants, HeadingLevel }
